import { useAuthStore } from '~/stores/auth'
import { cleanUrl, getCustomCookie } from './utils'

const TRAILING_SLASH_RE = /\/$/
const LEADING_SLASH_RE = /^\//

export class OscarClient {
  constructor(url, locale) {
    this.baseURL = url.replace(TRAILING_SLASH_RE, '')
    this.locale = locale

    if (true && typeof window !== 'undefined') {
      if (!window.productCache)
        window.productCache = {}
      if (!window.priceCache)
        window.priceCache = {}
      if (!window.availableCache)
        window.availableCache = {}
    }
  }

  _getHeaders() {
    const authStore = useAuthStore()
    const headers = {
      'Accept-Language': this.locale,
      'X-CSRFToken': true ? getCustomCookie('csrftoken') || '' : '',
    }

    if (!true) {
      headers.Host = new URL(this.baseURL).host
    }

    if (authStore?.authenticated && authStore?.token?.access) {
      headers['X-FORWARDED-ACCESS-TOKEN'] = authStore.token.access
    }

    return headers
  }

  async _request(method, path, options = {}) {
    const url = path.startsWith('http')
      ? path
      : `${this.baseURL}/${cleanUrl(path).replace(LEADING_SLASH_RE, '')}`

    const fetchOptions = {
      method,
      credentials: 'include',
      headers: { ...this._getHeaders(), ...options.headers },
      ...options,
    }

    if (options.body) {
      fetchOptions.body = options.body
    }

    if (options.params) {
      fetchOptions.query = options.params
    }

    try {
      const response = await $fetch(url, {
        method: fetchOptions.method,
        credentials: fetchOptions.credentials,
        headers: fetchOptions.headers,
        query: fetchOptions.query,
        body: fetchOptions.body,
      })

      return { data: response }
    }
    catch (err) {
      const status = err?.statusCode ?? err?.status
      if (
        status === 403
        && err?.data?.detail?.startsWith?.('CSRF')
        && !options._retry
      ) {
        await this.getUser()
        return this._request(method, path, { ...options, _retry: true })
      }

      console.error(err)
      throw err
    }
  }

  async getUser() {
    return this._request('GET', '/user/')
  }

  async logout() {
    return this._request('GET', '/logout/')
  }

  async getBasket() {
    return this._request('GET', '/basket/')
  }

  async search(q, filter, page) {
    return this._request('GET', '/search/full/', {
      params: { q, filter, page },
    })
  }

  async getLines(linesUrl) {
    return this._request('GET', cleanUrl(linesUrl))
  }

  async getProduct(productUrl) {
    const cacheString = `${productUrl}-${this.locale}`
    if (true && typeof window !== 'undefined' && Object.keys(window.productCache).includes(cacheString)) {
      return window.productCache[cacheString]
    }

    const res = await this._request('GET', cleanUrl(productUrl))
    if (true && typeof window !== 'undefined') {
      window.productCache[cacheString] = res
    }

    return res
  }

  async getMostPurchased() {
    if (true && typeof window !== 'undefined' && window.mostOrdered) {
      return window.mostOrdered
    }

    const res = await this._request('GET', '/orders/most-purchased/')
    if (true && typeof window !== 'undefined') {
      window.mostOrdered = res
    }

    return res
  }

  async addProductToBasket(productUrl, quantity, options) {
    const data = { url: productUrl, quantity }
    if (options) {
      data.options = options
    }

    return this._request('POST', '/basket/add-product/', { body: data })
  }

  async getAvailability(productId, specialPrice) {
    const url = specialPrice
      ? `products/${productId}/lightcustupdate/`
      : `products/${productId}/lightupdate/`

    const cacheKey = url + this.locale
    if (true && typeof window !== 'undefined' && Object.keys(window.availableCache).includes(cacheKey)) {
      return window.availableCache[cacheKey]
    }

    const res = await this._request('GET', cleanUrl(url))
    if (true && typeof window !== 'undefined') {
      window.availableCache[cacheKey] = res
    }

    return res
  }

  async getPrice(productId) {
    const url = `products/${productId}/price/`

    if (true && typeof window !== 'undefined' && Object.keys(window.priceCache).includes(url)) {
      return window.priceCache[url]
    }

    const res = await this._request('GET', cleanUrl(url))
    if (true && typeof window !== 'undefined') {
      window.priceCache[url] = res
    }

    return res
  }

  async deleteLine(lineUrl) {
    return this._request('DELETE', cleanUrl(lineUrl))
  }

  async updateLineQuantity(lineUrl, quantity) {
    return this._request('PATCH', cleanUrl(lineUrl), { body: { quantity } })
  }

  async getUserAddresses() {
    return this._request('GET', '/addresses/')
  }

  async createUpdateAddress(address) {
    const method = address.id ? 'PUT' : 'POST'
    const path = address.id ? `/addresses/${address.id}/` : '/addresses/'
    return this._request(method, path, { body: address })
  }

  async deleteAddress(id) {
    return this._request('DELETE', `/addresses/${id}/`)
  }

  async getShippingSlots(postcode) {
    return this._request('GET', `/shipping-slots/${postcode}/`)
  }

  async applyVoucher(code) {
    return this._request('POST', '/basket/voucher/', {
      body: { vouchercode: code },
    })
  }

  async removeVoucher(code) {
    return this._request('DELETE', `/basket/voucher/${code}/`)
  }

  async transmitSaferpayInformation(kind, orderNumber, hash) {
    return this._request('GET', `/payment/${kind}/${orderNumber}/${hash}/`)
  }

  async getSummary(slot) {
    return this._request('GET', `checkout/summary/${slot}/`)
  }

  async checkout(checkoutInfo) {
    return this._request('POST', '/checkout/', { body: checkoutInfo })
  }

  async cancelPaymentAndThawBasket(order) {
    return this._request(
      'POST',
      '/checkout/cancel-payment/',
      order ? { body: order } : {},
    )
  }

  async getOrders(limit, offset = 0) {
    return this._request('GET', '/orders/', {
      params: { limit, offset },
    })
  }

  async getOrder(number) {
    return this._request('GET', `orders/${number}/`)
  }

  async getSettings() {
    return this._request('GET', '/settings/')
  }
}
